import AddressAutocomplete from '@/components/AddressAutocomplete';
import { useAuth } from '@/contexts/AuthContext';
import { settingsService } from '@/services/settingsService';
import { tripService } from '@/services/tripService';
import { TripType } from '@/types';
import { GPSKalmanFilter, calculateDistance } from '@/utils/gpsFilter';
import { Edit2, MapPin, Navigation, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface DriveModeProps {
  onClose: () => void;
}

const DEFAULT_RATE = 3.79;
const DISTANCE_THRESHOLD = 0.025; // km — minimum increment to register
const MIN_SPEED_KMH = 5;
const MIN_UPDATE_INTERVAL_MS = 3000;

export default function DriveMode({ onClose }: DriveModeProps) {
  const { user } = useAuth();
  const [startLocation, setStartLocation] = useState('');
  const [destination, setDestination] = useState('');
  const [purpose, setPurpose] = useState('');
  const [tripType, setTripType] = useState<TripType>(TripType.Business);
  const [isEditingStart, setIsEditingStart] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDriving, setIsDriving] = useState(false);
  const [tripStartTime, setTripStartTime] = useState<Date | null>(null);
  const [currentRate, setCurrentRate] = useState(DEFAULT_RATE);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [currentPosition, setCurrentPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [trackingId, setTrackingId] = useState<number | null>(null);
  const [distanceTraveled, setDistanceTraveled] = useState(0);
  const [showDistanceChoice, setShowDistanceChoice] = useState(false);
  const [calculatedDistance, setCalculatedDistance] = useState<number | null>(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [positionUpdated, setPositionUpdated] = useState(false);
  const kalmanFilter = useRef(new GPSKalmanFilter());
  const lastUpdateTime = useRef<number>(Date.now());
  const currentPositionRef = useRef<{ lat: number; lng: number } | null>(null);

  // Keep ref in sync so GPS callback can see latest value without stale closure
  useEffect(() => {
    currentPositionRef.current = currentPosition;
  }, [currentPosition]);

  useEffect(() => {
    getCurrentLocation();
    loadRate();

    return () => {
      if (trackingId !== null) {
        navigator.geolocation.clearWatch(trackingId);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRate = async () => {
    try {
      const fiscalYears = await settingsService.getFiscalYears();
      const currentYear = new Date().getFullYear();
      const fy = fiscalYears.find(f => f.year === currentYear);
      if (fy) setCurrentRate(fy.rateUnder20000);
    } catch (err) {
      console.error('Error loading fiscal year rate:', err);
    }
  };

  const startGPSTracking = () => {
    if (!navigator.geolocation) return;

    kalmanFilter.current.reset();
    lastUpdateTime.current = Date.now();

    const id = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const filtered = kalmanFilter.current.filter(latitude, longitude, accuracy || 10);
        const newPosition = { lat: filtered.lat, lng: filtered.lng };
        const prev = currentPositionRef.current;

        if (prev) {
          const now = Date.now();
          const timeDiff = now - lastUpdateTime.current;
          if (timeDiff < MIN_UPDATE_INTERVAL_MS) return;

          const distanceIncrement = calculateDistance(prev.lat, prev.lng, filtered.lat, filtered.lng);
          const speedKmh = distanceIncrement / (timeDiff / (1000 * 60 * 60));

          if (distanceIncrement >= DISTANCE_THRESHOLD && speedKmh >= MIN_SPEED_KMH) {
            setDistanceTraveled(prev => prev + distanceIncrement);
            setCurrentPosition(newPosition);
            lastUpdateTime.current = now;
          }
        } else {
          setCurrentPosition(newPosition);
          lastUpdateTime.current = Date.now();
        }
      },
      (err) => { console.error('GPS tracking error:', err); },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 20000 }
    );

    setTrackingId(id);
  };

  const stopGPSTracking = () => {
    if (trackingId !== null) {
      navigator.geolocation.clearWatch(trackingId);
      setTrackingId(null);
    }
  };

  const getCurrentLocation = () => {
    setIsLoading(true);
    setLocationError(null);
    setPositionUpdated(false);

    if (!navigator.geolocation) {
      setLocationError('Din browser understøtter ikke GPS');
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentPosition({ lat: latitude, lng: longitude });

        try {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            setIsLoading(false);
            const address =
              status === google.maps.GeocoderStatus.OK && results?.[0]
                ? results[0].formatted_address
                : `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            setStartLocation(address);
            setPositionUpdated(true);
            setTimeout(() => setPositionUpdated(false), 3000);
          });
        } catch {
          setStartLocation(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          setIsLoading(false);
          setPositionUpdated(true);
          setTimeout(() => setPositionUpdated(false), 3000);
        }
      },
      (err) => {
        console.error('Geolocation error:', err);
        setLocationError('Kunne ikke hente din position. Indtast manuelt.');
        setIsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 }
    );
  };

  const handleStartDrive = () => {
    if (!destination.trim()) { alert('Indtast venligst en destination'); return; }
    if (!startLocation.trim()) { alert('Indtast venligst en startadresse'); return; }

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    let mapsUrl: string;

    if (isIOS) {
      mapsUrl = `maps://?saddr=${encodeURIComponent(startLocation)}&daddr=${encodeURIComponent(destination)}&dirflg=d`;
    } else if (isAndroid) {
      mapsUrl = `google.navigation:q=${encodeURIComponent(destination)}&mode=d`;
    } else {
      mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(startLocation)}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
    }

    window.location.href = mapsUrl;
    setIsDriving(true);
    setTripStartTime(new Date());
    setDistanceTraveled(0);
    startGPSTracking();
  };

  const handleCancelDrive = () => {
    if (confirm('Er du sikker på at du vil annullere kørslen? GPS-data vil gå tabt.')) {
      stopGPSTracking();
      setIsDriving(false);
      setTripStartTime(null);
      setDistanceTraveled(0);
      setPurpose('');
    }
  };

  const handleEndDrive = async () => {
    if (!purpose.trim()) { alert('Indtast venligst formål med turen'); return; }

    stopGPSTracking();
    setCalculatingDistance(true);

    try {
      const googleApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (!googleApiKey) throw new Error('Google Maps API-nøgle mangler (VITE_GOOGLE_MAPS_API_KEY)');

      const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': googleApiKey,
          'X-Goog-FieldMask': 'routes.distanceMeters',
        },
        body: JSON.stringify({
          origin: { address: startLocation },
          destination: { address: destination },
          travelMode: 'DRIVE',
          routingPreference: 'TRAFFIC_UNAWARE',
          computeAlternativeRoutes: false,
        }),
      });

      if (!response.ok) {
        const errBody = await response.text().catch(() => '');
        throw new Error(`Routes API ${response.status}: ${errBody.slice(0, 120)}`);
      }

      const data = await response.json();
      if (!data.routes?.length) throw new Error('Ingen rute fundet');

      setCalculatedDistance(Math.round(data.routes[0].distanceMeters / 1000));
      setShowDistanceChoice(true);
    } catch (err) {
      console.error('Error calculating distance:', err);
      setCalculatingDistance(false);

      if (distanceTraveled > 0.1) {
        if (confirm(`Kunne ikke beregne rute via Google Maps.\n\nVil du bruge GPS-tracket distance (${distanceTraveled.toFixed(2)} km)?`)) {
          await saveTrip(distanceTraveled, 'GPS-tracket');
        }
      } else {
        alert('Kunne ikke beregne distance og ingen GPS-data tilgængelig');
      }
    } finally {
      setCalculatingDistance(false);
    }
  };

  const saveTrip = async (distance: number, method: string) => {
    if (!user) { alert('Du skal være logget ind'); return; }

    setIsLoading(true);
    setShowDistanceChoice(false);

    try {
      const date = tripStartTime ?? new Date();
      const startTimeHHMM = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

      await tripService.createTrip({
        date: date.toISOString(),
        startAddress: startLocation,
        endAddress: destination,
        distance,
        purpose,
        type: tripType,
        startTime: startTimeHHMM,
      });

      const totalAmount = tripType === TripType.Business ? distance * currentRate : 0;
      alert(`Tur gemt! Distance (${method}): ${distance.toFixed(2)} km${totalAmount > 0 ? `, Beløb: ${totalAmount.toFixed(2)} kr` : ''}.`);
      onClose();
    } catch (err) {
      console.error('Error saving trip:', err);
      alert(`Fejl: ${err instanceof Error ? err.message : 'Ukendt fejl'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-auto">
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
        {/* Header */}
        <div className="sticky top-0 bg-white shadow-md z-10">
          <div className="flex items-center justify-between p-4 sm:p-5">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Auto kørsel</h1>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-gray-100 rounded-full transition-colors active:scale-95"
              aria-label="Luk"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 max-w-lg mx-auto">
          <div className="space-y-5">
            {/* Start location card */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span>Fra (Nuværende position)</span>
                </label>
                {!isEditingStart && (
                  <button
                    onClick={() => setIsEditingStart(true)}
                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <Edit2 className="w-4 h-4 text-gray-600" />
                  </button>
                )}
              </div>

              {isLoading && !startLocation ? (
                <div className="flex items-center space-x-2 text-blue-600 p-3 bg-blue-50 rounded-lg">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm">Henter position...</span>
                </div>
              ) : locationError ? (
                <div className="space-y-3">
                  <div className="text-sm text-red-600 mb-2 p-2 bg-red-50 rounded">{locationError}</div>
                  <button
                    onClick={getCurrentLocation}
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-sm shadow-sm hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <Navigation className="w-4 h-4" />
                    <span>Forsøg at hente position igen</span>
                  </button>
                  <div className="text-center text-sm text-gray-600">eller</div>
                  <AddressAutocomplete
                    value={startLocation}
                    onChange={setStartLocation}
                    placeholder="Indtast startadresse manuelt"
                    required
                  />
                </div>
              ) : isEditingStart ? (
                <div className="space-y-2">
                  <AddressAutocomplete
                    value={startLocation}
                    onChange={setStartLocation}
                    placeholder="Indtast startadresse"
                    required
                  />
                  <button
                    onClick={() => setIsEditingStart(false)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Gem
                  </button>
                </div>
              ) : (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-base text-gray-900">{startLocation}</p>
                </div>
              )}

              {!isEditingStart && startLocation && (
                <div className="mt-3 space-y-2">
                  <button
                    onClick={getCurrentLocation}
                    disabled={isLoading}
                    className="w-full bg-gray-100 text-gray-700 py-2.5 rounded-lg font-medium text-sm shadow-sm hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <Navigation className="w-4 h-4" />
                    <span>{isLoading ? 'Henter...' : 'Opdater position'}</span>
                  </button>
                  {positionUpdated && (
                    <div className="flex items-center justify-center space-x-2 text-green-600 text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Position opdateret</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Destination card */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex items-center space-x-2 mb-3">
                <Navigation className="w-4 h-4 text-red-600" />
                <span className="text-sm font-semibold text-gray-700">Til (Destination)</span>
              </div>
              <AddressAutocomplete
                value={destination}
                onChange={setDestination}
                placeholder="Indtast destination"
                disabled={isDriving}
                required
              />
            </div>

            {/* Start button or active driving view */}
            {!isDriving ? (
              <button
                onClick={handleStartDrive}
                disabled={!startLocation || !destination || isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-5 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-3"
              >
                <Navigation className="w-6 h-6" />
                <span>Start kørsel</span>
              </button>
            ) : (
              <div className="space-y-4">
                {/* GPS distance indicator */}
                <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-4">
                  <div className="flex items-center justify-center space-x-2 text-green-700 font-semibold mb-2">
                    <div className="animate-pulse w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>Kørsel i gang</span>
                  </div>
                  {distanceTraveled > 0 && (
                    <div className="text-center">
                      <p className="text-sm text-gray-600">GPS-tracket distance</p>
                      <p className="text-2xl font-bold text-green-700">{distanceTraveled.toFixed(2)} km</p>
                      {tripType === TripType.Business && (
                        <p className="text-xs text-gray-500 mt-1">
                          Beløb: {(distanceTraveled * currentRate).toFixed(2)} kr.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Purpose + trip type */}
                <div className="bg-white rounded-2xl shadow-md p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Formål</label>
                    <input
                      type="text"
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                      placeholder="F.eks. Kundemøde, Leverance, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                    <select
                      value={tripType}
                      onChange={(e) => setTripType(e.target.value as TripType)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                    >
                      <option value={TripType.Business}>Erhvervskørsel</option>
                      <option value={TripType.Personal}>Privat kørsel</option>
                      <option value={TripType.Commute}>Pendling</option>
                    </select>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelDrive}
                    disabled={isLoading}
                    className="flex-1 bg-gray-100 text-gray-700 py-4 rounded-2xl font-semibold text-base shadow-md hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Annuller
                  </button>
                  <button
                    onClick={handleEndDrive}
                    disabled={isLoading || calculatingDistance || !purpose.trim()}
                    className="flex-[2] bg-gradient-to-r from-green-600 to-green-700 text-white py-4 rounded-2xl font-bold text-base shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {calculatingDistance ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Beregner...</span>
                      </div>
                    ) : (
                      'Afslut og gem kørsel'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
            <p className="text-xs text-gray-600 text-center">
              Sats: {currentRate.toFixed(2)} kr/km
            </p>
          </div>
        </div>
      </div>

      {/* Distance choice modal */}
      {showDistanceChoice && calculatedDistance !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Vælg distance</h3>
            <p className="text-sm text-gray-600 mb-6">Vælg hvilken distance du vil bruge til turen:</p>

            <div className="space-y-3">
              {distanceTraveled > 0.1 && (
                <button
                  onClick={() => saveTrip(distanceTraveled, 'GPS-tracket')}
                  disabled={isLoading}
                  className="w-full p-4 border-2 border-blue-500 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 mb-1">GPS-tracket distance</div>
                    <div className="text-2xl font-bold text-blue-700">{distanceTraveled.toFixed(2)} km</div>
                    {tripType === TripType.Business && (
                      <div className="text-sm text-gray-600 mt-1">
                        Beløb: {(distanceTraveled * currentRate).toFixed(2)} kr
                      </div>
                    )}
                  </div>
                </button>
              )}

              <button
                onClick={() => saveTrip(calculatedDistance, 'Beregnet via Google Maps')}
                disabled={isLoading}
                className="w-full p-4 border-2 border-green-500 bg-green-50 rounded-xl hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="text-left">
                  <div className="font-semibold text-gray-900 mb-1">Beregnet via Google Maps</div>
                  <div className="text-2xl font-bold text-green-700">{calculatedDistance.toFixed(2)} km</div>
                  {tripType === TripType.Business && (
                    <div className="text-sm text-gray-600 mt-1">
                      Beløb: {(calculatedDistance * currentRate).toFixed(2)} kr
                    </div>
                  )}
                </div>
              </button>
            </div>

            <button
              onClick={() => {
                setShowDistanceChoice(false);
                setIsDriving(true);
                startGPSTracking();
              }}
              disabled={isLoading}
              className="mt-4 w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuller
            </button>

            {isLoading && (
              <div className="mt-4 flex items-center justify-center space-x-2 text-blue-600">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                <span className="text-sm">Gemmer...</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
